import React from "react";
import { Formik } from "formik";
import {
    TextField,
    withStyles,
    Button,
    Typography,
    MenuItem,
    FormControl,
    InputLabel,
    FormGroup,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    Paper,
    Select,
    Chip,
    Tooltip,
    Divider
} from "@material-ui/core";
import * as Yup from "yup";
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Info } from "@material-ui/icons";
import { BackButton } from '../../sc-utilities';
import appTranslations, { detectBrowserLanguageCode, withBrowserLanguageOnTop } from '../../sc-translate/appTranslations';

const styles = theme => ({
    root: {
        width: "auto",
        height: "auto",
        padding: theme.spacing(2),
        margin: theme.spacing(2)
    },
    rootEdit: {
        flexGrow: 1,
        paddingTop: theme.spacing(2)
    },
    submit: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        width: "100%",
        marginTop: "10px"
    },
    formControl: {
        width: "100%"
    },
    buttons: {
        textAlign: 'right'
    },
    subnet: {
        width: '98%'
    },
    formControlWithInfoField: {
        width: "100%",
        display: 'flex',
        flexDirection: 'row'
    },
    infoTooltip: {
        position: 'relative',
        marginTop: "38px",
        marginLeft: "15px"
    }
});

const renderInfoTooltip = (translations, currentIP, classes) => {
    let formHelperText = <>
        <Typography variant={'subtitle2'}>{translations['network_zone_subnet_info'] || 'Note: The network zone will work properly only if static IP address or subnet is used'}</Typography>
        <Typography variant={'subtitle2'}>{translations['network_zone_current_ip_info'] || 'Your current IP'}{`: ${currentIP}`}</Typography>
    </>
    return <Tooltip className={classes.infoTooltip} title={formHelperText} placement='bottom'>
        <Info />
    </Tooltip>
};

const NetworkZoneForm = props => {
    const { classes, formSettings, initialValues, handleSubmit, translations, permissions, currentIP } = props;

    return (
        <Paper className={classes.root}>
            {BackButton(translations)}
            <Formik
                enableReinitialize
                initialValues={initialValues}
                onSubmit={handleSubmit}

                validationSchema={Yup.object().shape(formSettings.Validation)}
            >
                {formikProps => {
                    const { values, touched, errors, isValid, dirty, isSubmitting, setFieldValue, handleChange, handleBlur, handleSubmit } = formikProps;
                    return (
                        <form onSubmit={handleSubmit}>
                            <Typography variant={"h6"}>{formSettings.FormTitle ? formSettings.FormTitle : null}</Typography>
                            <TextField
                                className={classes.input}
                                name="Name"
                                value={values.Name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Name && touched.Name)}
                                placeholder={translations["name"] || "Name"}
                                label={translations["name"] || "Name"}
                                margin={"dense"}
                                fullWidth={true}
                            />
                            <FormControl className={classes.formControlWithInfoField} margin={"dense"}>
                                <TextField
                                    name="Subnet"
                                    className={classes.subnet}
                                    value={values.Subnet}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!(errors.Subnet && touched.Subnet)}
                                    placeholder={translations["subnet"] || "Subnet"}
                                    label={translations["subnet"] || "Subnet"}
                                    margin={"dense"}
                                />
                                {renderInfoTooltip(translations, currentIP, classes)}
                            </FormControl>
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["select_priority"] || "Please select priority"}</InputLabel>
                                <Select
                                    name="Priority"
                                    label={translations["priority"] || "Priority"}
                                    margin={"dense"}
                                    value={values.Priority || 'low'}
                                    onChange={handleChange}
                                >
                                    <MenuItem key={'low'} value={'low'}>
                                        {translations["priority_low"] || `Low`}
                                    </MenuItem>
                                    <MenuItem key={'normal'} value={'normal'}>
                                        {translations["priority_normal"] || 'Normal'}
                                    </MenuItem>
                                    <MenuItem key={`high`} value={`high`}>
                                        {translations["priority_high"] || `High`}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["select_policy"] || "Please select policy"}</InputLabel>
                                <Select
                                    name="Policy"
                                    label={translations["policy"] || "Policy"}
                                    margin={"dense"}
                                    value={values.Policy || 'deny'}
                                    onChange={handleChange}
                                >
                                    <MenuItem key={'deny'} value={'deny'}>
                                        {translations["policy_deny"] || `Deny`}
                                    </MenuItem>
                                    <MenuItem key={'allow'} value={'allow'}>
                                        {translations["policy_allow"] || 'Allow'}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            margin={"dense"}
                                            name={"SpecifyPermissions"}
                                            checked={values.SpecifyPermissions}
                                            onChange={handleChange}
                                            color={'primary'}
                                        />
                                    }
                                    label={translations["specify_permissions"] || "Specify Permissions"}
                                />
                            </FormGroup>
                            <Autocomplete
                                name={'Permissions'}
                                multiple
                                disabled={!values.SpecifyPermissions}
                                id="PermissionsMultiSelect"
                                options={permissions || []}
                                getOptionLabel={(option) => option.label}
                                filterSelectedOptions
                                value={values.Permissions}
                                onChange={(_, value) => setFieldValue('Permissions', value)}
                                onBlur={handleBlur}
                                renderInput={(params) => (
                                    <TextField {...params} variant="standard" fullWidth={true} label={`${translations["choose_permissions"] || "Chose permissions"}...`} />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip label={option.label} {...getTagProps({ index })} />
                                    ))
                                }
                            />
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <Autocomplete
                                    options={withBrowserLanguageOnTop(appTranslations || [])}
                                    groupBy={option => option.__group}
                                    getOptionLabel={option => option.displayName || ''}
                                    getOptionSelected={(option, val) => option.code === val.code}
                                    value={
                                        (appTranslations || []).find(
                                            o => o.code === (values.DefaultLanguage || detectBrowserLanguageCode() || 'en')
                                        ) || null
                                    }
                                    onChange={(_event, option) => {
                                        setFieldValue('DefaultLanguage', option ? option.code : 'en');
                                    }}
                                    renderGroup={params => [
                                        params.group === 'All languages' && (
                                            <Divider key={`${params.key}-divider`} component="li" />
                                        ),
                                        <ul key={params.key} style={{ padding: 0, margin: 0, listStyle: 'none' }}>
                                            {params.children}
                                        </ul>
                                    ]}
                                    disableClearable
                                    blurOnSelect
                                    fullWidth
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            name="DefaultLanguage"
                                            label={translations["default_language"] || "Default language"}
                                            margin="dense"
                                        />
                                    )}
                                />
                            </FormControl>
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <Autocomplete
                                    options={withBrowserLanguageOnTop(appTranslations || [])}
                                    groupBy={option => option.__group}
                                    getOptionLabel={option => option.displayName || ''}
                                    getOptionSelected={(option, val) => option.code === val.code}
                                    value={
                                        (appTranslations || []).find(
                                            o => o.code === (values.DefaultKeyboardLayout || detectBrowserLanguageCode() || 'en')
                                        ) || null
                                    }
                                    onChange={(_event, option) => {
                                        setFieldValue('DefaultKeyboardLayout', option ? option.code : 'en');
                                    }}
                                    renderGroup={params => [
                                        params.group === 'All languages' && (
                                            <Divider key={`${params.key}-divider`} component="li" />
                                        ),
                                        <ul key={params.key} style={{ padding: 0, margin: 0, listStyle: 'none' }}>
                                            {params.children}
                                        </ul>
                                    ]}
                                    disableClearable
                                    blurOnSelect
                                    fullWidth
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            name="DefaultKeyboardLayout"
                                            label={translations["default_keyboard_layout"] || "Default keyboard layout"}
                                            margin="dense"
                                        />
                                    )}
                                />
                            </FormControl>
                            <div>
                                {
                                    formSettings.ButtonSubmit ? (
                                        <Button
                                            className={classes.submit}
                                            variant="contained"
                                            color={formSettings.ButtonSubmit.Color}
                                            type="submit"
                                            style={{ color: isSubmitting || !(isValid && dirty) ? null : 'white' }}
                                            disabled={isSubmitting || !(isValid && dirty)}
                                        >
                                            {isSubmitting ? <CircularProgress size={24} /> : formSettings.ButtonSubmit.Title}
                                        </Button>
                                    ) : null
                                }
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </Paper>
    );
};

export default withStyles(styles, { withTheme: true })(NetworkZoneForm);
